import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth, useCart } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Trash2, CreditCard, Loader2, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, clearCart, removeFromCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Review, 2: Info, 3: Payment
  const [orderComplete, setOrderComplete] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    postalCode: ''
  });

  const handleRemove = async (experienceId) => {
    try {
      await removeFromCart(experienceId);
      toast.success('Borttagen från varukorgen');
    } catch (err) {
      toast.error('Kunde inte ta bort');
    }
  };

  const handlePayment = async () => {
    if (!form.name || !form.email) {
      toast.error('Fyll i alla obligatoriska fält');
      return;
    }

    setLoading(true);
    try {
      // Create Klarna session (mocked)
      const sessionRes = await axios.post(`${API}/orders/create-session`, {
        items: cart.items.map(item => ({
          experience_id: item.experience_id,
          quantity: item.quantity,
          date: item.date
        })),
        customer_email: form.email,
        customer_name: form.name,
        billing_address: {
          street_address: form.address,
          city: form.city,
          postal_code: form.postalCode,
          country: 'SE'
        }
      });

      // Simulate Klarna authorization delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Confirm order
      await axios.post(`${API}/orders/confirm?session_id=${sessionRes.data.session_id}`);

      // Clear cart and show success
      await clearCart();
      setOrderComplete(true);
      toast.success('Beställning genomförd!');

    } catch (err) {
      toast.error(err.response?.data?.detail || 'Betalningen misslyckades');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8C8C8C] mb-4">Du måste vara inloggad för att gå till kassan</p>
          <Button onClick={() => navigate('/')} className="btn-primary">Till startsidan</Button>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]" data-testid="order-complete">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto p-8"
        >
          <div className="w-20 h-20 bg-[#4A6C48] rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h1 className="font-['Avenir_Next'] text-3xl text-[#1A1A1A] mb-4">
            Tack för din beställning!
          </h1>
          <p className="text-[#595959] mb-8">
            En bekräftelse har skickats till {form.email}. Vi ser fram emot att ge dig en fantastisk upplevelse!
          </p>
          <Button onClick={() => navigate('/upplevelser')} className="btn-primary">
            Fortsätt utforska
          </Button>
        </motion.div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#8C8C8C] mb-4">Din varukorg är tom</p>
          <Button onClick={() => navigate('/upplevelser')} className="btn-primary">Utforska upplevelser</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="checkout-page">
      <div className="container-main py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#8C8C8C] hover:text-[#2C3E36] mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Tillbaka
        </button>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            <h1 className="font-['Avenir_Next'] text-4xl text-[#1A1A1A]">Kassa</h1>

            {/* Progress steps */}
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                      step >= s ? 'bg-[#8B6F4E] text-white' : 'bg-[#E8E8E4] text-[#8C8C8C]'
                    }`}
                  >
                    {step > s ? <Check className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-[#8B6F4E]' : 'bg-[#E8E8E4]'}`} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Review cart */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <h2 className="font-medium text-xl">Granska din beställning</h2>
                {cart.items.map((item) => (
                  <div key={item.experience_id} className="flex gap-4 p-4 bg-white rounded-xl" data-testid={`checkout-item-${item.experience_id}`}>
                    <img
                      src={item.experience?.image_url}
                      alt={item.experience?.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-[#1A1A1A]">{item.experience?.name}</h4>
                      <p className="text-sm text-[#8C8C8C]">
                        {item.experience?.location} • {item.experience?.duration}
                      </p>
                      {item.date && <p className="text-sm text-[#2C3E36] mt-1">Datum: {item.date}</p>}
                      <p className="text-[#8B6F4E] font-semibold mt-2">{item.experience?.price} kr</p>
                    </div>
                    <button
                      onClick={() => handleRemove(item.experience_id)}
                      className="p-2 text-[#B83A3A] hover:bg-[#B83A3A]/10 rounded-lg transition-colors self-start"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                ))}
                <Button onClick={() => setStep(2)} className="btn-primary w-full h-14">
                  Fortsätt
                </Button>
              </motion.div>
            )}

            {/* Step 2: Customer info */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="font-medium text-xl">Dina uppgifter</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Namn *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="h-12 rounded-xl"
                      data-testid="checkout-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>E-post *</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-12 rounded-xl"
                      data-testid="checkout-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefon</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adress</Label>
                    <Input
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stad</Label>
                    <Input
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postnummer</Label>
                    <Input
                      value={form.postalCode}
                      onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Button onClick={() => setStep(1)} variant="outline" className="flex-1 h-14">
                    Tillbaka
                  </Button>
                  <Button onClick={() => setStep(3)} className="btn-primary flex-1 h-14">
                    Till betalning
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                <h2 className="font-medium text-xl">Betalning</h2>
                <div className="bg-white rounded-2xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="klarna-badge">Klarna</span>
                    <span className="text-sm text-[#8C8C8C]">Säker betalning</span>
                  </div>
                  <p className="text-[#595959]">
                    Välj att betala nu, senare eller dela upp betalningen med Klarna.
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 p-4 border border-[#8B6F4E]/20 rounded-xl cursor-pointer hover:bg-[#F9F9F7]">
                      <input type="radio" name="payment" defaultChecked className="w-5 h-5 accent-[#8B6F4E]" />
                      <div>
                        <p className="font-medium">Betala nu</p>
                        <p className="text-sm text-[#8C8C8C]">Kort eller direktbetalning</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border border-[#8B6F4E]/20 rounded-xl cursor-pointer hover:bg-[#F9F9F7]">
                      <input type="radio" name="payment" className="w-5 h-5 accent-[#8B6F4E]" />
                      <div>
                        <p className="font-medium">Betala senare</p>
                        <p className="text-sm text-[#8C8C8C]">Faktura inom 14 dagar</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 p-4 border border-[#8B6F4E]/20 rounded-xl cursor-pointer hover:bg-[#F9F9F7]">
                      <input type="radio" name="payment" className="w-5 h-5 accent-[#8B6F4E]" />
                      <div>
                        <p className="font-medium">Dela upp betalningen</p>
                        <p className="text-sm text-[#8C8C8C]">3 räntefria delbetalningar</p>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Button onClick={() => setStep(2)} variant="outline" className="flex-1 h-14">
                    Tillbaka
                  </Button>
                  <Button
                    onClick={handlePayment}
                    disabled={loading}
                    className="btn-primary flex-1 h-14"
                    data-testid="complete-payment"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Slutför köp
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-28 bg-white rounded-2xl p-6 space-y-4">
              <h3 className="font-['Avenir_Next'] text-xl">Ordersammanfattning</h3>
              <div className="space-y-3 border-b border-[#8B6F4E]/10 pb-4">
                {cart.items.map((item) => (
                  <div key={item.experience_id} className="flex justify-between text-sm">
                    <span className="text-[#595959]">{item.experience?.name}</span>
                    <span className="font-medium">{item.experience?.price} kr</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between">
                <span className="text-[#8C8C8C]">Totalt</span>
                <span className="font-['Avenir_Next'] text-2xl text-[#8B6F4E]">
                  {cart.total.toLocaleString('sv-SE')} kr
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#4A6C48] text-sm">
                <ShieldCheck className="w-4 h-4" />
                <span>Säker betalning med SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
